import { AuthBaseClient } from '../base-client';
import { startCallResponseSchema, type StartCallResponse } from '../browser-call/types';
import {
  workflowDetailSchema,
  workflowListSchema,
  workflowProblemListSchema,
  workflowStepSchema,
  type CreateStepBody,
  type CreateWorkflowBody,
  type PublishWorkflowBody,
  type TestWorkflowBody,
  type WorkflowTestResult,
  type WorkflowTestInputSpecs,
  workflowTestInputSpecsSchema,
  workflowTestResultSchema,
  type WorkflowTestCapture,
  workflowTestCaptureSchema,
  type UpdateStepBody,
  type UpdateWorkflowBody,
  type WorkflowDetail,
  type WorkflowProblem,
  type WorkflowStep,
  type WorkflowSummary,
} from './types';

const BASE = '/api/workflow-defns';

/**
 * Client for the conversation builder's workflows (`/api/workflow-defns`).
 * Every response is Zod-parsed at the boundary.
 */
export class WorkflowDefnsClient extends AuthBaseClient {
  async list(signal?: AbortSignal): Promise<WorkflowSummary[]> {
    return workflowListSchema.parse(await this.get<unknown>(BASE, signal));
  }

  async getById(id: string, signal?: AbortSignal): Promise<WorkflowDetail> {
    return workflowDetailSchema.parse(await this.get<unknown>(`${BASE}/${id}`, signal));
  }

  /** The draft's problems, without the rest of the workflow. */
  async check(id: string, signal?: AbortSignal): Promise<WorkflowProblem[]> {
    return workflowProblemListSchema.parse(
      await this.get<unknown>(`${BASE}/${id}/problems`, signal)
    );
  }

  async create(body: CreateWorkflowBody): Promise<WorkflowDetail> {
    return workflowDetailSchema.parse(await this.post<unknown>(BASE, body));
  }

  async update(id: string, body: UpdateWorkflowBody): Promise<WorkflowDetail> {
    return workflowDetailSchema.parse(await this.patch<unknown>(`${BASE}/${id}`, body));
  }

  async publish(id: string, body: PublishWorkflowBody): Promise<WorkflowDetail> {
    return workflowDetailSchema.parse(await this.post<unknown>(`${BASE}/${id}/publish`, body));
  }

  async archive(id: string): Promise<void> {
    await this.delete<unknown>(`${BASE}/${id}`);
  }

  async createStep(workflowId: string, body: CreateStepBody): Promise<WorkflowStep> {
    return workflowStepSchema.parse(await this.post<unknown>(`${BASE}/${workflowId}/steps`, body));
  }

  async updateStep(
    workflowId: string,
    stepId: string,
    body: UpdateStepBody
  ): Promise<WorkflowStep> {
    return workflowStepSchema.parse(
      await this.patch<unknown>(`${BASE}/${workflowId}/steps/${stepId}`, body)
    );
  }

  /**
   * Start a browser test call on the current draft (or one step of it), as
   * the builder's fictional test patient. Join the returned room to talk to it.
   */
  async test(id: string, body: TestWorkflowBody): Promise<StartCallResponse> {
    return startCallResponseSchema.parse(await this.post<unknown>(`${BASE}/${id}/test`, body));
  }

  /** What a test of the draft, or of `nodeId`, asks the tester for (ADR-306). */
  async testInputs(
    id: string,
    nodeId: string | undefined,
    signal?: AbortSignal
  ): Promise<WorkflowTestInputSpecs> {
    const query = nodeId ? `?nodeId=${encodeURIComponent(nodeId)}` : '';
    return workflowTestInputSpecsSchema.parse(
      await this.get<unknown>(`${BASE}/${id}/test-inputs${query}`, signal)
    );
  }

  /** What a finished test call understood and withheld (ADR-306). */
  async testResult(
    id: string,
    interactionId: string,
    signal?: AbortSignal
  ): Promise<WorkflowTestResult> {
    return workflowTestResultSchema.parse(
      await this.get<unknown>(`${BASE}/${id}/tests/${interactionId}`, signal)
    );
  }

  /** Everything a finished test call left behind, for a ticket (ADR-309). */
  async testCapture(
    id: string,
    interactionId: string,
    signal?: AbortSignal
  ): Promise<WorkflowTestCapture> {
    return workflowTestCaptureSchema.parse(
      await this.get<unknown>(`${BASE}/${id}/tests/${interactionId}/capture`, signal)
    );
  }
}
